/**
 * useOrgAdmin — Hooks para endpoints /api/org-admin/*
 * Story SAAS-3.4 / EPIC-SAAS-001
 *
 * Wrappers tanstack-query sobre os endpoints administrativos escopados.
 * Funciona para super_admin (cross-org) e org_admin (escopado à sua org).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { handleUnauthorized } from '@/lib/api/session-guard';
import { useTenant } from '@/contexts/TenantContext';

const API_URL = import.meta.env?.VITE_API_URL || 'http://localhost:3001';
const TOKEN_KEY = 'auth_token';

interface OrgTenant {
  id: string;
  slug: string;
  nome: string;
  tipo: 'organization' | 'loja';
  parentTenantId: string | null;
  gestaoclickLojaId: string | null;
  ativo: boolean;
  criadoEm: string;
}

interface OrgUser {
  id: string;
  userId: string;
  nomeCompleto: string;
  role: 'admin' | 'gerente' | 'producao' | 'super_admin' | 'org_admin';
  tenantId: string | null;
  setores: string[] | null;
  modulos: string[] | null;
  ativo: boolean;
}

async function http<T = any>(method: string, path: string, body?: any): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) handleUnauthorized(path); // CLAUDE.md §10: 401 encerra a sessão
  const json = await res.json();
  if (!res.ok || json.error) {
    throw new Error(json.error?.message || `HTTP ${res.status}`);
  }
  return json.data;
}

// ─── Organizations ────────────────────────────────────────────────────

export function useOrganizations() {
  return useQuery<OrgTenant[]>({
    queryKey: ['org-admin', 'organizations'],
    queryFn: () => http<OrgTenant[]>('GET', '/api/org-admin/organizations'),
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { slug: string; nome: string }) =>
      http('POST', '/api/org-admin/organizations', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-admin', 'organizations'] });
    },
  });
}

export function useUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...updates }: { id: string; nome?: string; ativo?: boolean }) =>
      http('PUT', `/api/org-admin/organizations/${id}`, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-admin', 'organizations'] });
    },
  });
}

// ─── Lojas ────────────────────────────────────────────────────────────

export function useLojas(organizationIdFilter?: string) {
  const { isSuperAdmin } = useTenant();
  const queryString = isSuperAdmin && organizationIdFilter
    ? `?organizationId=${organizationIdFilter}`
    : '';

  return useQuery<OrgTenant[]>({
    queryKey: ['org-admin', 'lojas', organizationIdFilter ?? null],
    queryFn: () => http<OrgTenant[]>('GET', `/api/org-admin/lojas${queryString}`),
  });
}

export function useCreateLoja() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { slug: string; nome: string; parentTenantId?: string; gestaoclickLojaId?: string }) =>
      http('POST', '/api/org-admin/lojas', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-admin', 'lojas'] });
    },
  });
}

export function useUpdateLoja() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...updates }: { id: string; nome?: string; ativo?: boolean; gestaoclickLojaId?: string | null }) =>
      http('PUT', `/api/org-admin/lojas/${id}`, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-admin', 'lojas'] });
    },
  });
}

// ─── Users ────────────────────────────────────────────────────────────

export function useOrgUsers(organizationIdFilter?: string) {
  const { isSuperAdmin } = useTenant();
  const queryString = isSuperAdmin && organizationIdFilter
    ? `?organizationId=${organizationIdFilter}`
    : '';

  return useQuery<OrgUser[]>({
    queryKey: ['org-admin', 'users', organizationIdFilter ?? null],
    queryFn: () => http<OrgUser[]>('GET', `/api/org-admin/users${queryString}`),
  });
}

export interface CreateOrgUserInput {
  email: string;
  password: string;
  nomeCompleto: string;
  role: 'admin' | 'gerente' | 'producao' | 'org_admin';
  tenantId: string;
  setores?: string[];
  modulos?: string[];
}

export function useCreateOrgUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrgUserInput) =>
      http('POST', '/api/org-admin/users', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-admin', 'users'] });
    },
  });
}

export function useUpdateOrgUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...updates }: { userId: string } & Partial<Omit<CreateOrgUserInput, 'email'>> & { ativo?: boolean }) =>
      http('PUT', `/api/org-admin/users/${userId}`, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-admin', 'users'] });
    },
  });
}

export function useDeleteOrgUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      http('DELETE', `/api/org-admin/users/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-admin', 'users'] });
    },
  });
}

// ─── Stats ────────────────────────────────────────────────────────────

export function useOrgStats(organizationIdFilter?: string) {
  const { isSuperAdmin } = useTenant();
  const queryString = isSuperAdmin && organizationIdFilter
    ? `?organizationId=${organizationIdFilter}`
    : '';

  return useQuery({
    queryKey: ['org-admin', 'stats', organizationIdFilter ?? null],
    queryFn: () => http('GET', `/api/org-admin/stats${queryString}`),
  });
}

export type { OrgTenant, OrgUser };
